import React from 'react'
import CurrencyInput from './CurrencyInput'

export default function FormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  options = [],
  hint,
  required = false,
  error,
  placeholder,
  rows = 3,
  accept,
  ...props
}) {
  const inputId = `field-${name}`
  const baseInputClasses = "block w-full rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
  const normalClasses = "border-gray-300"
  const errorClasses = "border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
  const inputClasses = `${baseInputClasses} ${error ? errorClasses : normalClasses}`

  const handleChange = (e) => {
    if (type === 'checkbox') {
      onChange({ target: { name, value: e.target.checked } })
    } else if (type === 'file') {
      onChange(e.target.files[0] || null)
    } else {
      onChange(e)
    }
  }

  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            id={inputId}
            name={name}
            value={value || ''}
            onChange={handleChange}
            rows={rows}
            placeholder={placeholder}
            required={required}
            className={inputClasses}
            {...props}
          />
        )

      case 'select':
        return (
          <select
            id={inputId}
            name={name}
            value={value || ''}
            onChange={handleChange}
            required={required}
            className={inputClasses}
            {...props}
          >
            {!options.some(opt => (typeof opt === 'object' ? opt.value : opt) === '') && (
              <option value="">Select...</option>
            )}
            {options.map((opt) => {
              const optValue = typeof opt === 'object' ? opt.value : opt
              const optLabel = typeof opt === 'object' ? opt.label : opt
              return (
                <option key={optValue} value={optValue}>
                  {optLabel}
                </option>
              )
            })}
          </select>
        )

      case 'checkbox':
        return (
          <div className="flex items-start">
            <input
              id={inputId}
              name={name}
              type="checkbox"
              checked={value || false}
              onChange={handleChange}
              className="h-4 w-4 mt-0.5 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              {...props}
            />
            <div className="ml-2">
              <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
                {label}
              </label>
              {hint && (
                <p className="text-sm text-gray-500">{hint}</p>
              )}
            </div>
          </div>
        )

      case 'checkboxGroup':
        return (
          <div className="space-y-2">
            {options.map((opt) => {
              const optValue = typeof opt === 'object' ? opt.value : opt
              const optLabel = typeof opt === 'object' ? opt.label : opt
              const isChecked = Array.isArray(value) && value.includes(optValue)
              return (
                <div key={optValue} className="flex items-center">
                  <input
                    id={`${inputId}-${optValue}`}
                    name={name}
                    type="checkbox"
                    value={optValue}
                    checked={isChecked}
                    onChange={(e) => {
                      const currentValues = Array.isArray(value) ? [...value] : []
                      if (e.target.checked) {
                        onChange({ target: { name, value: [...currentValues, optValue] } })
                      } else {
                        onChange({ target: { name, value: currentValues.filter(v => v !== optValue) } })
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <label htmlFor={`${inputId}-${optValue}`} className="ml-2 text-sm text-gray-700 capitalize">
                    {optLabel}
                  </label>
                </div>
              )
            })}
          </div>
        )

      case 'file':
        return (
          <input
            id={inputId}
            name={name}
            type="file"
            onChange={handleChange}
            accept={accept}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
            {...props}
          />
        )

      case 'currency':
        return (
          <CurrencyInput
            inputId={inputId}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className={inputClasses}
            {...props}
          />
        )

      default:
        return (
          <input
            id={inputId}
            name={name}
            type={type}
            value={value || ''}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            className={inputClasses}
            {...props}
          />
        )
    }
  }

  return (
    <div className="mb-4">
      {type !== 'checkbox' && (
        <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {renderInput()}

      {type !== 'checkbox' && hint && (
        <p className="mt-1 text-sm text-gray-500">{hint}</p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}
